import React, { ReactNode } from 'react';
import { Box, Typography, Avatar, IconButton, Tooltip, Button } from '@mui/material';
import {
  Menu as MenuIcon,
  School as SchoolIcon,
  CoPresent as CoPresentIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { ThemeName } from '../../../theme/themeConfig';
import { UserInfo } from '../../../types';
import { getPhotoUrl } from '../../../utils/formatting';
import HuttonsWhite from '../../../img/Huttons_white.png';
import HuttonsBlue from '../../../img/Huttons_blue.png';

interface AppHeaderProps {
  currentTheme: ThemeName;
  userInfo: UserInfo | null;
  sidebarOpen: boolean;
  onSidebarToggle: () => void;
  onThemeToggle: () => void;
  onTrainingClick?: () => void;
  showTrainingOnMobile?: boolean;
  onTutorialClick?: () => void;
  tutorialStatus?: 'not-started' | 'in-progress' | 'completed';
  showTutorialButton?: boolean;
  appLabel?: string;
  actions?: ReactNode;
  onUserClick?: (event: React.MouseEvent<HTMLElement>) => void;
  userMenuOpen?: boolean;
  showUserMenuIndicator?: boolean;
  hideUserOnMobile?: boolean;
  hideBrandOnMobile?: boolean;
  compactOnMobile?: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  currentTheme,
  userInfo,
  sidebarOpen,
  onSidebarToggle,
  onThemeToggle,
  onTrainingClick,
  showTrainingOnMobile = true,
  onTutorialClick,
  tutorialStatus = 'not-started',
  showTutorialButton = false,
  appLabel = 'SUPER Picker',
  actions,
  onUserClick,
  userMenuOpen = false,
  showUserMenuIndicator = false,
  hideUserOnMobile = false,
  hideBrandOnMobile = false,
  compactOnMobile = false,
}) => {
  const userPhotoUrl = getPhotoUrl(userInfo?.Photo);

  const getTutorialColor = () => {
    if (tutorialStatus === 'completed') return 'success';
    if (tutorialStatus === 'in-progress') return 'warning';
    return 'error';
  };

  const getTutorialTooltip = () => {
    if (tutorialStatus === 'completed') return 'Tutorial (Completed)';
    if (tutorialStatus === 'in-progress') return 'Tutorial (In Progress)';
    return 'Tutorial';
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 64,
        bgcolor: 'var(--color-surface)',
        color: 'var(--color-text-strong)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        pl: { xs: compactOnMobile ? 0.5 : 1, sm: compactOnMobile ? 1 : 2, md: 4 },
        pr: { xs: compactOnMobile ? 0.5 : 1, sm: compactOnMobile ? 1 : 3, md: 5 },
        zIndex: (theme) => theme.zIndex.appBar,
        boxShadow: '0 2px 4px rgba(var(--color-shadow-rgb), 0.3)',
      }}
    >
      {/* Left Side - Menu Toggle & Branding */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: compactOnMobile ? 0.5 : 1.5, md: 1.5 }, flexShrink: 0 }}>
        <IconButton
          onClick={onSidebarToggle}
          sx={{
            color: 'var(--color-text-strong)',
            width: { xs: compactOnMobile ? 32 : 48, md: 48 },
            height: { xs: compactOnMobile ? 32 : 48, md: 48 },
            p: { xs: compactOnMobile ? 0.5 : 1, md: 1 },
            '&:hover': {
              bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
            },
          }}
          aria-label="Toggle sidebar"
        >
          <MenuIcon />
        </IconButton>
        <Box
          component="img"
          src={currentTheme === 'dark' ? HuttonsWhite : HuttonsBlue}
          alt="Huttons"
          sx={{
            height: { xs: compactOnMobile ? 20 : 28, md: 28 },
            width: 'auto',
            display: { xs: hideBrandOnMobile ? 'none' : 'block', sm: 'block' },
          }}
        />
        <Typography
          component="span"
          sx={{
            color: 'var(--color-text-strong)',
            fontSize: '0.9rem',
            fontWeight: 400,
            display: { xs: 'none', sm: compactOnMobile ? 'none' : 'inline', md: 'inline' },
            whiteSpace: 'nowrap',
          }}
        >
          | {appLabel}
        </Typography>
      </Box>

      {/* Right Side - Tutorial & User Profile */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 0.5, sm: compactOnMobile ? 0.5 : 1.5, md: 1.5 },
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexShrink: 0,
          overflow: 'visible',
          width: 'auto',
          height: 'auto',
        }}
      >
        {actions}
        {showTutorialButton && onTutorialClick && (
          <>
            <Tooltip title={getTutorialTooltip()}>
              <IconButton
                onClick={onTutorialClick}
                color={getTutorialColor()}
                aria-label={getTutorialTooltip()}
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  width: 34,
                  height: 34,
                  border: 'none',
                  borderRadius: 1,
                }}
              >
                <SchoolIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={getTutorialTooltip()}>
              <Button
                variant="outlined"
                size="small"
                color={getTutorialColor()}
                startIcon={<SchoolIcon fontSize="small" />}
                onClick={onTutorialClick}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'currentColor',
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                Tutorial
              </Button>
            </Tooltip>
          </>
        )}
        {onTrainingClick && (
          <>
            <Tooltip title="Training">
              <IconButton
                onClick={onTrainingClick}
              sx={{
                  display: {
                    xs: showTrainingOnMobile ? 'inline-flex' : 'none',
                    sm: compactOnMobile && showTrainingOnMobile ? 'inline-flex' : 'none',
                    md: 'none',
                  },
                  color: 'var(--color-text-strong)',
                  border: 'none',
                  borderRadius: 1,
                  width: 34,
                  height: 34,
                  '&:hover': {
                    borderColor: 'var(--color-accent)',
                    bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
                  },
                }}
                aria-label="Training"
              >
                <CoPresentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CoPresentIcon fontSize="small" />}
              onClick={onTrainingClick}
              sx={{
                color: 'var(--color-text-strong)',
                borderColor: 'rgba(var(--color-text-rgb), 0.3)',
                textTransform: 'none',
                fontWeight: 600,
                display: { xs: 'none', sm: compactOnMobile ? 'none' : 'inline-flex', md: 'inline-flex' },
                '&:hover': {
                  borderColor: 'var(--color-accent)',
                  bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
                },
              }}
            >
              Training
            </Button>
          </>
        )}
        <Tooltip title={currentTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
          <IconButton
            onClick={onThemeToggle}
            sx={{
              color: 'var(--color-text-strong)',
              width: { xs: compactOnMobile ? 32 : 48, md: 48 },
              height: { xs: compactOnMobile ? 32 : 48, md: 48 },
              p: { xs: compactOnMobile ? 0.5 : 1, md: 1 },
              '&:hover': {
                bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
              },
            }}
            aria-label={currentTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {currentTheme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
        <Box
          onClick={onUserClick}
          aria-haspopup={onUserClick ? 'menu' : undefined}
          aria-expanded={onUserClick ? userMenuOpen : undefined}
          sx={{
            display: { xs: hideUserOnMobile ? 'none' : 'inline-flex', sm: 'inline-flex' },
            alignItems: 'center',
            gap: { xs: 0, sm: 1 },
            px: { xs: compactOnMobile ? 0.25 : 0.5, sm: compactOnMobile ? 0.25 : 1.5, md: 1.5 },
            py: 0.75,
            borderRadius: 1,
            cursor: onUserClick ? 'pointer' : 'default',
            '&:hover': onUserClick ? { bgcolor: 'rgba(var(--color-text-rgb), 0.1)' } : undefined,
          }}
        >
          <Avatar
            src={userPhotoUrl}
            sx={{
              width: { xs: compactOnMobile ? 28 : 32, md: 32 },
              height: { xs: compactOnMobile ? 28 : 32, md: 32 },
              bgcolor: 'rgba(var(--color-text-rgb), 0.2)',
              fontSize: '0.85rem',
              color: 'var(--color-text-strong)',
              '& img': { objectFit: 'contain', objectPosition: 'center top' },
            }}
          >
            {(userInfo?.Name || 'User')
              .split(' ')
              .map((part) => part[0])
              .slice(0, 2)
              .join('')
              .toUpperCase()}
          </Avatar>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              color: 'var(--color-text-strong)',
              display: { xs: 'none', sm: compactOnMobile ? 'none' : 'block', md: 'block' },
              whiteSpace: 'nowrap',
            }}
          >
            {userInfo?.Name || 'User'}
          </Typography>
          {showUserMenuIndicator && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: { xs: 'none', sm: 'inline-block' },
                width: 8,
                height: 8,
                borderRight: '2px solid currentColor',
                borderBottom: '2px solid currentColor',
                transform: userMenuOpen ? 'rotate(225deg)' : 'rotate(45deg)',
                transition: 'transform 0.15s ease',
                mb: userMenuOpen ? -0.5 : 0.5,
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppHeader;
